import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react/macro";
import {
  AlertCircle,
  ArrowRight,
  Clock,
  MapPin,
  Music,
  Repeat,
  User,
} from "lucide-react";
import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import {
  getTranslations,
  useSupportedInstruments,
} from "../../api/instrumentApi.ts";
import { useUserSubscriptionQuery } from "../../api/subscriptionApi.ts";
import {
  useTeacherLocationsQuery,
  useTeacherQuery,
} from "../../api/teacherApi.ts";
import { useLanguageStore } from "../../stores/languageStore.ts";
import { weekdays } from "../Weekdays.ts";

const SubscriptionSuccess: React.FC = () => {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const navigate = useNavigate();
  const { language } = useLanguageStore();
  const { i18n } = useLingui();

  const {
    data: subscription,
    isError: subscriptionError,
    isLoading: subscriptionLoading,
  } = useUserSubscriptionQuery(subscriptionId);

  const { data: teacher } = useTeacherQuery(subscription?.teacherId);

  const { data: locationsData } = useTeacherLocationsQuery(
    subscription?.teacherId ?? "",
  );

  const { data: supportedInstruments } = useSupportedInstruments();

  const teacherName = useMemo(
    () => (teacher ? `${teacher.firstName} ${teacher.lastName}` : undefined),
    [teacher],
  );

  const profilePictureUrl = useMemo(
    () => teacher?.profilePictureUrl,
    [teacher],
  );

  const locationName = useMemo(() => {
    if (!locationsData?.locations || !subscription?.locationId)
      return undefined;
    return locationsData.locations.find((l) => l.id === subscription.locationId)
      ?.name;
  }, [locationsData, subscription?.locationId]);

  const translatedInstrument = useMemo(() => {
    if (!subscription || !supportedInstruments) return "";
    return getTranslations(
      supportedInstruments,
      subscription.instrument,
      language,
    );
  }, [supportedInstruments, subscription, language]);

  const translatedDay = useMemo(() => {
    if (!subscription) return "";
    const weekday = weekdays.find((w) => w.value === subscription.dayOfWeek);
    return weekday ? i18n._(weekday.label) : subscription.dayOfWeek;
  }, [subscription, i18n]);

  const monthlyPrice = useMemo(() => {
    if (!subscription) return "0.00";
    return subscription.price?.toFixed(2) ?? "0.00";
  }, [subscription]);

  const getProfilePicture = () => {
    return profilePictureUrl ? (
      <img
        src={profilePictureUrl}
        alt=""
        className="h-6 w-6 rounded-full object-cover"
      />
    ) : (
      <User className="h-6 w-6" />
    );
  };

  const handleGoToSubscriptions = () => {
    navigate("/mystudio/subscriptions");
  };

  if (!subscriptionId) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-red-600" />
            <p className="font-medium text-red-800">
              <Trans>
                Invalid subscription ID. Please check your link and try again.
              </Trans>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (subscriptionLoading) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="flex min-h-64 items-center justify-center">
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-blue-500"></div>
            <p className="text-gray-600">
              <Trans>Loading subscription details...</Trans>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (subscriptionError || !subscription) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-red-600" />
            <div>
              <p className="font-medium text-red-800">
                <Trans>Error loading subscription details</Trans>
              </p>
              <p className="mt-1 text-sm text-red-700">
                <Trans>
                  Your subscription request was sent, but we couldn't load the
                  details. Please check your subscriptions page.
                </Trans>
              </p>
            </div>
          </div>
          <button
            onClick={handleGoToSubscriptions}
            className="bg-soluud-primary hover:bg-soluud-primary-hover active:bg-soluud-primary-active mt-4 inline-flex items-center gap-2 rounded-lg px-4 py-2 font-medium text-white shadow-sm transition-all duration-150 active:scale-[0.98]"
          >
            <Trans>Go to Subscription Overview</Trans>
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl p-6">
      {/* Pending Header */}
      <div className="mb-8 text-center">
        <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
          <Clock className="h-10 w-10 text-blue-600" />
        </div>
        <h1 className="mb-2 text-3xl font-bold text-gray-900">
          <Trans>Subscription Requested!</Trans>
        </h1>
        <p className="text-lg text-gray-600">
          <Trans>Your subscription request has been sent to the teacher.</Trans>
        </p>
      </div>

      {/* Subscription Details */}
      <div className="mb-8 rounded-lg border-2 border-blue-200 bg-blue-50 p-6">
        <div className="mb-4 flex items-center gap-2">
          <Repeat className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-blue-900">
            <Trans>Subscription Details</Trans>
          </h2>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Music className="h-6 w-6 shrink-0 text-blue-600" />
              <div>
                <p className="text-sm text-blue-700">
                  <Trans>Instrument</Trans>
                </p>
                <p className="font-semibold text-blue-900">
                  {translatedInstrument}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="shrink-0 text-blue-600">
                {getProfilePicture()}
              </div>
              <div>
                <p className="text-sm text-blue-700">
                  <Trans>Teacher</Trans>
                </p>
                <p className="font-semibold text-blue-900">
                  {teacherName ?? "—"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <MapPin className="h-6 w-6 shrink-0 text-blue-600" />
              <div>
                <p className="text-sm text-blue-700">
                  <Trans>Location</Trans>
                </p>
                <p className="font-semibold text-blue-900">
                  {locationName ?? "—"}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Clock className="h-6 w-6 shrink-0 text-blue-600" />
              <div>
                <p className="text-sm text-blue-700">
                  <Trans>Day</Trans>
                </p>
                <p className="font-semibold text-blue-900">{translatedDay}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Clock className="h-6 w-6 shrink-0 text-blue-600 opacity-0" />
              <div>
                <p className="text-sm text-blue-700">
                  <Trans>Time</Trans>
                </p>
                <p className="font-semibold text-blue-900">
                  {subscription.from} – {subscription.to}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 border-t border-blue-200 pt-4">
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold text-blue-900">
              <Trans>Monthly Price</Trans>
            </span>
            <span className="text-2xl font-bold text-blue-900">
              €{monthlyPrice}
            </span>
          </div>
        </div>
      </div>

      {/* What's Next */}
      <div className="mb-8 rounded-lg border border-gray-200 bg-gray-50 p-6">
        <h3 className="mb-3 text-lg font-semibold text-gray-900">
          <Trans>What's Next?</Trans>
        </h3>
        <div className="space-y-2 text-gray-700">
          <p>
            •{" "}
            <Trans>
              Your teacher has been informed about your subscription request and
              will accept it soon.
            </Trans>
          </p>
          <p>
            •{" "}
            <Trans>
              Once accepted, you will receive a notification and will need to
              add your payment information.
            </Trans>
          </p>
          <p>
            •{" "}
            <Trans>
              After adding payment, your weekly lessons will be automatically
              scheduled.
            </Trans>
          </p>
        </div>
      </div>

      {/* Action Button */}
      <div className="text-center">
        <button
          onClick={handleGoToSubscriptions}
          className="bg-soluud-primary hover:bg-soluud-primary-hover active:bg-soluud-primary-active inline-flex items-center gap-2 rounded-lg px-6 py-3 text-lg font-medium text-white shadow-sm transition-all duration-150 active:scale-[0.98]"
        >
          <Trans>Go to Subscription Overview</Trans>
          <ArrowRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
